import React, { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { Form, Formik, FormikProps } from "formik";
import Button, { ButtonThemes } from "../../../../ui-components/button/button";
import { ItemModel } from "../../../../app/data/common/models";
import { BolItemSchema } from "../../../../app/data/bol/models";
import BolState from "../../../../slices/bol/BolState";
import {
  bolSelector
} from "../../../../slices/bol/bolSlice";
import FreightItemFormFields from "../../../common/freight-item/freightItemFormFields";
import { freightItemValues } from "../../../../app/data/common/freightItem";

interface BolItemFormProps {
  itemIndex?: number,
  editMode?: boolean;
  onCancel?: () => void,
  onSubmit: (data: ItemModel) => void
}

let initialValues: ItemModel = freightItemValues;

const BolItemForm: React.FC<BolItemFormProps> = (props) => {
  const {
    itemIndex,
    editMode,
    onCancel,
    onSubmit
  } = props;
  const bolState: BolState = useSelector(bolSelector);
  const bolItemRef = useRef<any>(null);

  useEffect(() => {
    if (itemIndex === undefined) {
      // Formik has issue when passing undefined values (https://github.com/formium/formik/issues/2332), so we passing empty strings  
      initialValues = freightItemValues;
      bolItemRef.current.resetForm({ values: initialValues });
    } else {
      initialValues = {
        packageType: bolState.items[itemIndex].packageType,
        units: bolState.items[itemIndex].units || "",
        ratingClass: bolState.items[itemIndex].ratingClass || "",
        description: bolState.items[itemIndex].description || "",
        notes: bolState.items[itemIndex].notes || "",
        yards: bolState.items[itemIndex].yards || "",
        weight: bolState.items[itemIndex].weight || "",
        length: bolState.items[itemIndex].length || "",
        width: bolState.items[itemIndex].width || "",
        height: bolState.items[itemIndex].height || "",
        diameter: bolState.items[itemIndex].diameter || "",
        sidemark: bolState.items[itemIndex].sidemark || "",
        commodityCode: bolState.items[itemIndex].commodityCode,
      };

      bolItemRef.current.resetForm({ values: initialValues });

      if (bolState.quoteNumber) {
        const notesField = document.getElementById("xgs-bol__item__notes--js") as HTMLInputElement;
        if (notesField) notesField.focus();
      }
    };
    // eslint-disable-next-line
  }, [itemIndex]);

  return (
    <div className="xgs-bol__item-form">
      <Formik
        onSubmit={onSubmit}
        initialValues={initialValues}
        validationSchema={BolItemSchema}
        innerRef={bolItemRef}
        enableReinitialize
      >
        {(props: FormikProps<ItemModel>) => (
          <Form>
            <FreightItemFormFields
              forCustomer
              quoteNumber={bolState.quoteNumber}
            />

            <div className="xgs-bol__item-form__misc-text">
              <ul>
                <li>If <strong>description</strong> isn't provided it will be listed as &quot;Miscellaneous&quot;.</li>
                <li>Any <strong>notes</strong> added will appear under the line item on the bill of lading.</li>
                <li><strong>Sidemark</strong> will appear on bill of lading and shipping label.</li>
              </ul>
            </div>
            <div className="xgs-bol__item-form__row">
              <Button
                type="submit"
                theme={ButtonThemes.blue}
                disabled={
                  !props.isValid ||
                  !props.values.units ||
                  !!((editMode || itemIndex === undefined) && !props.dirty)
                }
                className="xgs-bol__item-form__button"
              >
                {editMode ? "Save changes" : "Add item"}
              </Button>
              {editMode && (
                <Button
                  type="button"
                  theme={ButtonThemes.blue}
                  onClick={onCancel}
                  className="xgs-bol__item-form__button"
                >
                  Cancel
                </Button>
              )}
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default BolItemForm;